import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { useTranslation } from 'react-i18next';
import { Split, FlaskConical } from 'lucide-react';

interface ABTestNodeData {
  label: string;
  type: string;
  icon?: string;
  config: Record<string, any>;
  isConfigured?: boolean;
}

const ABTestNode: React.FC<NodeProps<ABTestNodeData>> = ({ data, selected }) => {
  const { t } = useTranslation();

  const getConfigPreview = () => {
    if (data.config?.splitA && data.config?.splitB) {
      return `A: ${data.config.splitA}% | B: ${data.config.splitB}%`;
    }
    if (data.config?.trafficSplit) {
      return `A: ${data.config.trafficSplit}% | B: ${100 - data.config.trafficSplit}%`;
    }
    return 'A: 50% | B: 50%';
  };

  const configPreview = getConfigPreview();
  const splitA = data.config?.trafficSplit || data.config?.splitA || 50;
  const splitB = 100 - splitA;

  return (
    <div
      className={`relative min-w-[200px] bg-white dark:bg-gray-800 rounded-xl shadow-lg border-2 transition-all ${
        selected
          ? 'border-purple-500 shadow-purple-100 dark:shadow-purple-900/30'
          : 'border-purple-200 dark:border-purple-800 hover:border-purple-400'
      }`}
    >
      {/* Handle - Input */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-4 !h-4 !bg-purple-500 !border-2 !border-white dark:!border-gray-800 !rounded-full"
      />

      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500 to-indigo-500 rounded-t-lg px-4 py-2">
        <div className="flex items-center gap-2 text-white">
          <FlaskConical className="w-4 h-4" />
          <span className="text-xs font-medium uppercase tracking-wide">{t('workflow.abTest.title', 'Teste A/B')}</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center text-purple-600 dark:text-purple-400">
            <Split className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-gray-900 dark:text-white text-sm leading-tight">
              {data.label || t('workflow.abTest.title', 'Teste A/B')}
            </p>
            {data.isConfigured ? (
              <span className="text-xs text-purple-600 dark:text-purple-400">{t('workflow.configured', 'Configurado')}</span>
            ) : (
              <span className="text-xs text-yellow-600 dark:text-yellow-400">{t('workflow.clickToConfigure', 'Clique para configurar')}</span>
            )}
          </div>
        </div>

        {/* Traffic Split Preview */}
        <div className="mt-3 p-2 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
          <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
            <span>{t('workflow.abTest.trafficSplit', 'Divisao de trafego')}</span>
          </div>
          <div className="flex h-2 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700">
            <div
              className="bg-gradient-to-r from-blue-500 to-blue-600 transition-all"
              style={{ width: `${splitA}%` }}
            />
            <div
              className="bg-gradient-to-r from-green-500 to-green-600 transition-all"
              style={{ width: `${splitB}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center font-mono">
            {configPreview}
          </p>
        </div>

        {/* Branch Labels */}
        <div className="mt-3 flex justify-between text-xs">
          <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
            <div className="w-2 h-2 rounded-full bg-blue-500" />
            {t('workflow.abTest.variantA', 'Variante A')}
          </div>
          <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
            {t('workflow.abTest.variantB', 'Variante B')}
            <div className="w-2 h-2 rounded-full bg-green-500" />
          </div>
        </div>
      </div>

      {/* Handles - Two Outputs for A/B */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="variant_a"
        className="!w-4 !h-4 !bg-blue-500 !border-2 !border-white dark:!border-gray-800 !rounded-full !left-[30%]"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="variant_b"
        className="!w-4 !h-4 !bg-green-500 !border-2 !border-white dark:!border-gray-800 !rounded-full !left-[70%]"
      />

      {/* Flask Animation */}
      <div className="absolute top-3 right-3">
        <div className="w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
          <FlaskConical className="w-3.5 h-3.5 text-purple-500" />
        </div>
      </div>
    </div>
  );
};

export default memo(ABTestNode);
